import { ButtonHTMLAttributes, HTMLAttributes, forwardRef, ReactNode, useId } from 'react'
import { ChevronRightIcon, InfoIcon } from './icons'

interface FormCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const FormCard = forwardRef<HTMLDivElement, FormCardProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`bg-background-primary border border-border-primary border-solid flex flex-col gap-0 items-start px-[20px] py-[24px] relative rounded-2xl shadow-xs shrink-0 w-full max-w-[768px] ${className}`}
        {...props}
      >
        {children}
      </div>
    )
  }
)
FormCard.displayName = 'FormCard'

interface AlertCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const AlertCard = forwardRef<HTMLDivElement, AlertCardProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`bg-background-primary border border-border-primary border-solid flex flex-col gap-[16px] items-start p-[16px] relative rounded-xl shadow-xs shrink-0 w-full max-w-[768px] ${className}`}
        {...props}
      >
        {children}
      </div>
    )
  }
)
AlertCard.displayName = 'AlertCard'

interface InfoAlertProps {
  message: string
  description?: string
  iconColor?: 'info' | 'warning'
}

export function InfoAlert({ message, description, iconColor = 'info' }: InfoAlertProps) {
  const iconClassName =
    iconColor === 'warning'
      ? 'size-5 shrink-0 text-warning-primary'
      : 'size-5 shrink-0 text-text-tertiary-600'

  return (
    <AlertCard>
      <div className="flex gap-[12px] items-start w-full">
        <InfoIcon className={iconClassName} aria-hidden />
        <div className="flex flex-col gap-[4px] min-w-0 flex-1">
          <p className="font-body font-semibold text-sm leading-sm text-text-secondary-700 w-full">
            {message}
          </p>
          {description && (
            <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 w-full">
              {description}
            </p>
          )}
        </div>
      </div>
    </AlertCard>
  )
}

interface ActionBarProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  justify?: 'between' | 'start' | 'end'
}

export const ActionBar = forwardRef<HTMLDivElement, ActionBarProps>(
  ({ children, className = '', justify = 'between', ...props }, ref) => {
    const justifyClass = {
      between: 'justify-between',
      start: 'justify-start',
      end: 'justify-end',
    }[justify]

    return (
      <div
        ref={ref}
        className={`border-t border-border-secondary flex items-center ${justifyClass} max-w-[768px] px-[16px] py-[12px] relative shrink-0 w-full ${className}`}
        {...props}
      >
        {children}
      </div>
    )
  }
)
ActionBar.displayName = 'ActionBar'

interface SelectableCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  headline: string
  caption?: string
  rightAdornment?: ReactNode
  /** Larger padding and typography — use for primary branching choices (e.g. claim entry). */
  prominent?: boolean
  /** Pinned “chosen” styling (e.g. while a confirmation modal is open). */
  selected?: boolean
}

export const SelectableCard = forwardRef<HTMLButtonElement, SelectableCardProps>(
  (
    { headline, caption, rightAdornment, prominent = false, selected = false, className = '', ...props },
    ref
  ) => {
    const captionId = useId()
    const density = prominent
      ? 'min-h-[104px] md:min-h-[120px] px-[18px] md:px-[20px] py-[20px] md:py-[24px] gap-[14px] md:gap-[16px] items-start md:items-center'
      : 'px-[16px] py-[16px] gap-[12px] items-center'
    const headlineClass = prominent
      ? selected
        ? 'font-body font-semibold text-lg leading-snug md:text-xl md:leading-[28px] text-text-primary-on-brand'
        : 'font-body font-semibold text-lg leading-snug md:text-xl md:leading-[28px] text-text-primary-900 transition-colors duration-150 group-hover:text-text-primary-on-brand group-active:text-text-primary-on-brand'
      : selected
        ? 'font-body font-semibold text-md leading-md text-text-primary-on-brand'
        : 'font-body font-semibold text-md leading-md text-text-primary-900 transition-colors duration-150 group-active:text-text-primary-on-brand'
    const captionClass = prominent
      ? selected
        ? 'font-body font-regular text-sm leading-snug md:text-md md:leading-md text-text-primary-on-brand opacity-90'
        : 'font-body font-regular text-sm leading-snug md:text-md md:leading-md text-text-tertiary-600 transition-colors duration-150 group-hover:text-text-primary-on-brand group-active:text-text-primary-on-brand group-active:opacity-90'
      : selected
        ? 'font-body font-regular text-xs md:text-sm md:leading-sm text-text-primary-on-brand opacity-90'
        : 'font-body font-regular text-xs md:text-sm md:leading-sm text-text-tertiary-600 transition-colors duration-150 group-active:text-text-primary-on-brand group-active:opacity-90'

    const surfaceClass = selected
      ? 'bg-background-brand-solid border-background-brand-solid shadow-none hover:bg-background-brand-solid hover:border-background-brand-solid scale-100'
      : prominent
        ? 'bg-background-primary border-border-primary hover:bg-background-brand-solid hover:border-background-brand-solid active:bg-background-brand-solid active:border-background-brand-solid active:shadow-none active:scale-[0.99]'
        : 'bg-background-primary border-border-primary hover:bg-background-secondary hover:border-border-secondary active:bg-background-brand-solid active:border-background-brand-solid active:shadow-none active:scale-[0.99]'

    return (
      <button
        ref={ref}
        type="button"
        aria-label={headline}
        aria-pressed={selected ? true : undefined}
        aria-describedby={caption ? captionId : undefined}
        className={`group border border-solid rounded-2xl shadow-xs w-full flex text-left cursor-pointer transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2 ${surfaceClass} ${density} ${className}`}
        {...props}
      >
        <div className={`flex flex-col flex-1 min-w-0 ${prominent ? 'gap-[8px] md:gap-[10px]' : 'gap-[4px]'}`}>
          <p className={headlineClass}>{headline}</p>
          {caption && (
            <p id={captionId} className={captionClass}>
              {caption}
            </p>
          )}
        </div>
        {!prominent || rightAdornment != null ? (
          <div
            className={`shrink-0 self-center transition-colors duration-150 ${selected ? 'text-text-primary-on-brand' : 'text-text-primary-900 group-active:text-text-primary-on-brand'}`}
          >
            {rightAdornment ?? (
              <ChevronRightIcon data-testid="selectable-card-chevron" />
            )}
          </div>
        ) : null}
      </button>
    )
  }
)
SelectableCard.displayName = 'SelectableCard'
